import React from 'react';
import { ImageResizeMode, StyleProp, View, ViewStyle } from 'react-native';
import { MB_PressableStateCallbackType } from '@/components/general/pressable';
import { MB_SVG_BrokenFile } from '@/components/general/svg-components';

type MB_ImageSource =
  | {
      uri?: string | undefined;
      headers?: {
        [key: string]: string;
      };
    }
  | number;

interface MB_ImageObject {
  small?: string;
  medium?: string;
  large?: string;
}

interface MB_ImageProps {
  /**
   * Callback called when hovering over the image.
   */
  onHoverChange?: (state?: MB_PressableStateCallbackType) => void;

  /**
   * Callback called when the image is pressed.
   */
  onPress?: () => void;

  /**
   * Callback called when the image is long pressed.
   */
  onLongPress?: () => void;

  /**
   * Disable interaction with the image.
   */
  disableInteraction?: boolean;

  /**
   * Allow the image to be presented in a modal when pressed.
   */
  allowModalPresentationOnPress?: boolean;

  /**
   * Style of the image.
   */
  style?: StyleProp<ViewStyle>;

  /**
   * The mechanism that should be used to resize the image when the image's dimensions
   * differ from the image view's dimensions. Defaults to `auto`.
   *
   * - `auto`: Use heuristics to pick between `resize` and `scale`.
   *
   * - `resize`: A software operation which changes the encoded image in memory before it
   * gets decoded. This should be used instead of `scale` when the image is much larger
   * than the view.
   *
   * - `scale`: The image gets drawn downscaled or upscaled. Compared to `resize`, `scale` is
   * faster (usually hardware accelerated) and produces higher quality images. This
   * should be used if the image is smaller than the view. It should also be used if the
   * image is slightly bigger than the view.
   */
  resizeMethod?: 'auto' | 'resize' | 'scale' | undefined;

  /**
   * How the image should be resized.
   *  - 'contain':  Scale the image uniformly (maintain the image's aspect ratio)
   * so that both dimensions (width and height) of the image will be equal to or
   * less than the corresponding dimension of the view (minus padding).
   *  - 'cover': Scale the image uniformly (maintain the image's aspect ratio) so
   * that both dimensions (width and height) of the image will be equal to or larger
   * than the corresponding dimension of the view (minus padding).
   *  - 'stretch': Scale width and height independently, This may change the aspect
   * ratio of the src.
   *  - 'center': Do not scale the image, keep centered.
   *  - 'repeat': Doesn't work on web. Currently, gets set to 'cover' on mobile.
   */
  resizeMode?: ImageResizeMode | undefined;

  /**
   * The source of the image.
   */
  source: MB_ImageSource | string;

  /**
   * Disable animations when hovering over the image on web.
   */
  disableOnHoverAnimations?: boolean;

  /**
   * Programmatically activate the hover effect on web.
   */
  activateHover?: boolean;

  /**
   * Grayscale filter to apply to the image's style on web.
   */
  grayscaleFilter?: number;

  /**
   * Set the priority to loading the image on mobile.
   */
  mobileLoadingPriority?: 'low' | 'normal' | 'high';
  brokenImageComponent?: React.JSX.Element;
  disableBrokenImage?: boolean;
}

type ImageGetterOptions = { asUri?: boolean; preferLarge?: boolean } | undefined;
type ImageGetterReturn<T extends ImageGetterOptions> = (T extends { asUri: true } ? { uri: string } : string) | undefined;

const mbGetImage = <T extends ImageGetterOptions>({
  image,
  options,
  size,
}: {
  image: MB_ImageObject | undefined;
  size: 'small' | 'medium' | 'large';
  options?: T;
}): ImageGetterReturn<T> => {
  switch (size) {
    case 'small':
      return mbGetMediumImage(image, options);
    case 'medium':
      return mbGetMediumImage(image, options);
    case 'large':
      return mbGetLargestImage(image, options);
  }
};

const mbGetMediumImage = <T extends ImageGetterOptions>(image: MB_ImageObject | undefined, options?: T): ImageGetterReturn<T> => {
  if (!image) {
    return undefined;
  }

  function processReturn(retVal: string) {
    return !options?.asUri ? retVal : { uri: retVal };
  }

  if (image.medium) {
    return processReturn(image.medium) as ImageGetterReturn<T>;
  }

  if (options?.preferLarge) {
    if (image.large !== undefined) {
      return processReturn(image.large) as ImageGetterReturn<T>;
    } else if (image.small !== undefined) {
      return processReturn(image.small) as ImageGetterReturn<T>;
    }
  } else {
    if (image.small !== undefined) {
      return processReturn(image.small) as ImageGetterReturn<T>;
    } else if (image.large !== undefined) {
      return processReturn(image.large) as ImageGetterReturn<T>;
    }
  }

  return undefined;
};

const mbGetLargestImage = <T extends ImageGetterOptions>(image: MB_ImageObject | undefined, options?: T): ImageGetterReturn<T> => {
  if (!image) {
    return undefined;
  }

  function processReturn(retVal: string) {
    return !options?.asUri ? retVal : { uri: retVal };
  }

  if (image.large !== undefined) {
    return processReturn(image.large) as ImageGetterReturn<T>;
  } else if (image.medium !== undefined) {
    return processReturn(image.medium) as ImageGetterReturn<T>;
  } else if (image.small !== undefined) {
    return processReturn(image.small) as ImageGetterReturn<T>;
  }

  return undefined;
};

const mbGetSmallestImage = <T extends ImageGetterOptions>(image: MB_ImageObject | undefined, options?: T): ImageGetterReturn<T> => {
  if (!image) {
    return undefined;
  }

  function processReturn(retVal: string) {
    return !options?.asUri ? retVal : { uri: retVal };
  }

  if (image.small !== undefined) {
    return processReturn(image.small) as ImageGetterReturn<T>;
  } else if (image.medium !== undefined) {
    return processReturn(image.medium) as ImageGetterReturn<T>;
  } else if (image.large !== undefined) {
    return processReturn(image.large) as ImageGetterReturn<T>;
  }

  return undefined;
};

const brokenImageComponent = (customComponent?: React.JSX.Element) => {
  if (customComponent) {
    return customComponent;
  }

  return (
    <View style={{ width: '100%', height: '100%', alignItems: 'center', justifyContent: 'center' }}>
      <MB_SVG_BrokenFile size={32} color="black" />
    </View>
  );
};

export { mbGetSmallestImage, mbGetMediumImage, mbGetLargestImage, mbGetImage, brokenImageComponent };
export type { MB_ImageProps, MB_ImageSource, MB_ImageObject };
